/** LMSR cost C(q) = b * logsumexp(q/b) — numerically stable (matches trading UI / backend). */
export function lmsrCost(b: number, shares: number[]): number {
  if (!b || shares.length === 0) return 0;
  const m = Math.max(...shares.map((q) => q / b));
  const s = shares.reduce((t, q) => t + Math.exp(q / b - m), 0);
  return b * (m + Math.log(Math.max(s, 1e-300)));
}

/** Instantaneous price of outcome idx (probability mass, R1 per share at resolution). */
export function lmsrPrice(b: number, shares: number[], idx: number): number {
  if (!b || shares.length === 0) return 1 / Math.max(shares.length, 1);
  const m = Math.max(...shares.map((q) => q / b));
  const exps = shares.map((q) => Math.exp(q / b - m));
  const total = exps.reduce((a, v) => a + v, 0);
  return total > 0 ? exps[idx] / total : 1 / shares.length;
}

/** Gross ZAR proceeds from selling `delta` shares on `optionIdx` (before performance fee). */
export function lmsrGrossSellProceeds(
  b: number,
  shares: number[],
  optionIdx: number,
  delta: number,
): number {
  if (!b || delta <= 0 || optionIdx < 0 || optionIdx >= shares.length) return 0;
  const before = lmsrCost(b, shares);
  const next = [...shares];
  next[optionIdx] -= delta;
  if (next[optionIdx] < 0) return 0;
  return before - lmsrCost(b, next);
}

/**
 * Shares you receive for a ZAR buy budget: finds Δq so C(q+Δ)−C(q) = `budget`.
 * Matches `place-bet` Edge and `quote_lmsr_buy` (64 iterations, hi = budget×100).
 */
export function solveSharesOutForLMSRBuy(
  b: number,
  shares: number[],
  optionIdx: number,
  budget: number,
): number {
  if (!b || budget <= 0 || optionIdx < 0 || optionIdx >= shares.length) return 0;
  const costBefore = lmsrCost(b, shares);
  let lo = 0;
  let hi = budget * 100;
  for (let i = 0; i < 64; i++) {
    const mid = (lo + hi) / 2;
    const newShares = [...shares];
    newShares[optionIdx] = shares[optionIdx] + mid;
    const cost = lmsrCost(b, newShares) - costBefore;
    if (cost < budget) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * Dynamic spread rate for a given outcome probability.
 * Returns the rate used to inflate the cost-per-share.
 */
export function getDynamicSpreadRate(
  probability: number,
  maxRate: number,
  minRate: number,
  lowThreshold: number = 0.5,
  highThreshold: number = 0.85,
): number {
  const low = Number.isFinite(lowThreshold) ? lowThreshold : 0.5;
  const high = Number.isFinite(highThreshold) ? highThreshold : 0.85;
  const lo = Math.min(low, high);
  const hi = Math.max(low, high);
  if (probability <= lo) return maxRate;
  if (probability >= hi) return minRate;
  const span = Math.max(1e-12, hi - lo);
  const rate = maxRate - ((maxRate - minRate) * ((probability - lo) / span));
  return Math.max(minRate, Math.min(maxRate, rate));
}
